import { useState } from 'react'
import { Hash, KeyRound, Pencil, X } from 'lucide-react'
import { MriButton, MriInput, cn } from '@mriqbox/ui-kit'
import { isRoomSlug, slugify } from '@shared/protocol'

export interface RoomEditorValues {
    name?: string
    /**
     * Endereco novo — sempre o slug do nome, nunca um valor a parte. So vai
     * quando a pessoa pede: trocar quebra o link antigo.
     */
    code?: string
    /** Ausente mantem o PIN, string vazia remove, string troca. */
    pin?: string
}

export interface RoomEditorProps {
    room: { code: string; name: string; requiresPin: boolean }
    /** Ha gente na sala agora? Ai o endereco nao pode mudar. */
    emUso?: boolean
    onSave: (fields: RoomEditorValues) => void
    onCancel: () => void
    className?: string
}

/**
 * Edicao de uma sala fixa: nome, endereco e PIN.
 *
 * O mesmo formulario serve a tela inicial e a propria sala — quem e dono
 * costuma perceber que quer mudar o nome justamente quando esta lá dentro, e
 * mandar a pessoa sair pra isso seria um recado estranho.
 *
 * O PIN nunca volta do servidor (so o hash fica guardado), entao o campo comeca
 * vazio mesmo numa sala que ja tem tranca: campo vazio com a protecao ligada
 * significa "mantem o que existe", nao "remove".
 */
export function RoomEditor({ room, emUso = false, onSave, onCancel, className }: RoomEditorProps) {
    const [name, setName] = useState(room.name)
    const [usePin, setUsePin] = useState(room.requiresPin)
    const [pin, setPin] = useState('')
    /**
     * O endereco so muda quando a pessoa pede. Segui-lo do nome derrubaria o
     * link que ela ja mandou pros outros sem ela ter pedido nada.
     */
    const [trocarEndereco, setTrocarEndereco] = useState(false)

    const cleanName = name.trim()
    const cleanPin = pin.trim()
    /**
     * O endereco sai do NOME, e nao de um campo separado.
     *
     * Deixar escolher os dois fazia a mesma sala ter dois nomes — o da tela e o
     * do link — e essa divergencia so cresce, ate o endereco nao dizer mais
     * nada sobre a sala. O servidor recusa qualquer outro destino.
     */
    const novoCode = slugify(cleanName)
    const enderecoDivergiu = novoCode !== room.code && isRoomSlug(novoCode)

    // Ligar a protecao numa sala que nao tinha exige um PIN de verdade; numa
    // que ja tinha, o campo vazio so quer dizer "nao mexe".
    const precisaDePin = usePin && !room.requiresPin
    const pinCurto = !!cleanPin && cleanPin.length < 4
    const podeSalvar = !!cleanName
        && !pinCurto
        && (!precisaDePin || cleanPin.length >= 4)

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault()
        if (!podeSalvar) return

        const fields: RoomEditorValues = {}
        if (cleanName !== room.name) fields.name = cleanName
        if (trocarEndereco && enderecoDivergiu) fields.code = novoCode
        if (!usePin) {
            // So manda a remocao quando havia mesmo o que remover.
            if (room.requiresPin) fields.pin = ''
        } else if (cleanPin) {
            fields.pin = cleanPin
        }

        // Nada mudou: poupa a ida ao servidor e fecha o formulario.
        if (Object.keys(fields).length > 0) onSave(fields)
        else onCancel()
    }

    return (
        <form
            onSubmit={handleSubmit}
            className={cn(
                'animate-in fade-in zoom-in-95 space-y-3 rounded-xl border border-primary/30 bg-card/95 p-3 duration-150',
                className
            )}
        >
            <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                    <Pencil className="h-3.5 w-3.5" /> Editar sala
                </span>
                <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Fechar"
                    className="text-muted-foreground hover:text-foreground"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>

            <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Nome da sala
                </label>
                <div className="relative">
                    <span className="absolute inset-y-0 left-2.5 flex items-center text-muted-foreground">
                        <Hash className="h-3.5 w-3.5" />
                    </span>
                    <MriInput
                        value={name}
                        maxLength={60}
                        autoFocus
                        className="pl-8 text-xs"
                        onChange={(event) => setName(event.target.value)}
                    />
                </div>
            </div>

            <div className="rounded-lg border border-border/70 bg-background/50 p-2.5">
                <p className="truncate font-mono text-[11px] text-foreground">
                    /sala/{trocarEndereco && enderecoDivergiu ? novoCode : room.code}
                </p>

                {enderecoDivergiu ? (
                    <>
                        <label className="mt-1.5 flex cursor-pointer items-start gap-2">
                            <input
                                type="checkbox"
                                checked={trocarEndereco}
                                disabled={emUso}
                                onChange={(event) => setTrocarEndereco(event.target.checked)}
                                className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-border accent-primary disabled:cursor-not-allowed"
                            />
                            <span className="text-[10px] leading-relaxed text-muted-foreground">
                                Atualizar o endereço para{' '}
                                <span className="font-mono text-foreground">/sala/{novoCode}</span>,
                                acompanhando o nome novo.
                                {emUso && ' Só com a sala vazia.'}
                            </span>
                        </label>
                        {trocarEndereco && !emUso && (
                            <p className="mt-1 text-[10px] text-amber-400">
                                Ao salvar, o link antigo para de funcionar.
                            </p>
                        )}
                    </>
                ) : (
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                        O endereço acompanha o nome da sala.
                    </p>
                )}
            </div>

            <div className="rounded-lg border border-border/70 bg-background/50 p-2.5">
                <label className="flex cursor-pointer items-center justify-between">
                    <div className="flex items-center gap-2">
                        <KeyRound className={cn('h-4 w-4', usePin ? 'text-amber-400' : 'text-muted-foreground')} />
                        <div>
                            <p className="text-xs font-semibold text-foreground">Proteger com PIN</p>
                            <p className="text-[10px] text-muted-foreground">
                                {room.requiresPin ? 'Esta sala pede PIN hoje' : 'Esta sala está aberta hoje'}
                            </p>
                        </div>
                    </div>
                    <input
                        type="checkbox"
                        checked={usePin}
                        onChange={(event) => setUsePin(event.target.checked)}
                        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                    />
                </label>

                {usePin && (
                    <div className="mt-2.5 animate-in fade-in slide-in-from-top-1 border-t border-border/60 pt-2.5 duration-150">
                        <MriInput
                            value={pin}
                            maxLength={12}
                            className="text-xs"
                            placeholder={room.requiresPin
                                ? 'Deixe em branco para manter o PIN atual'
                                : 'Defina o PIN (4 a 12 caracteres)'}
                            onChange={(event) => setPin(event.target.value)}
                        />
                        {pinCurto && (
                            <p className="mt-1 text-[10px] text-destructive">O PIN precisa ter ao menos 4 caracteres.</p>
                        )}
                    </div>
                )}

                {!usePin && room.requiresPin && (
                    <p className="mt-2 text-[10px] text-amber-400">
                        Ao salvar, qualquer pessoa com o link entra sem PIN.
                    </p>
                )}
            </div>

            <div className="flex gap-2 pt-1">
                <MriButton type="submit" size="sm" className="flex-1 font-semibold" disabled={!podeSalvar}>
                    Salvar
                </MriButton>
                <MriButton type="button" variant="ghost" size="sm" onClick={onCancel}>
                    Cancelar
                </MriButton>
            </div>
        </form>
    )
}
